/**
 * Design tokens for the location analytics page.
 *
 * The dashboard's redesign (MainLayout / LocationsPage / LocationRoute) has no
 * MUI theme behind it — each screen states its type and colour inline. Analytics
 * is the densest surface in the app, so the values it would otherwise repeat
 * across a dozen charts live here instead.
 */

export const headingFont = "'Bricolage Grotesque', sans-serif";
export const bodyFont = "'Instrument Sans', system-ui, sans-serif";

export const ink = {
  /** Headings and figures. */
  strong: '#17222C',
  /** Body copy. */
  body: '#46525C',
  /** Labels and captions. */
  muted: '#5F6B76',
  /** Eyebrow labels, axis ticks, disabled text. */
  faint: '#7C8893'
};

export const surface = {
  card: '#fff',
  /** Wells inside a card — sub-stats, empty states. */
  sunken: '#F7FAFC',
  border: '#E6ECF0',
  /** Interactive borders: inputs, segmented controls. */
  borderStrong: '#CBD8E1',
  hover: '#F9FBFC',
  grid: '#EEF2F5'
};

export const accent = {
  /** Brand blue — the mark's colour, kept for emphasis only. */
  brand: '#30BAF3',
  mid: '#1778A8',
  deep: '#0E4F70',
  wash: '#EAF6FC'
};

/**
 * Categorical series colours. Stamps own the house blue; the two reward series
 * sit far enough away in both hue and lightness to stay separable under the
 * common forms of colour blindness and in greyscale.
 */
export const series = {
  stamps: '#1778A8',
  issued: '#D98324',
  redeemed: '#2E8B62'
};

/** Sequential ramp for the ordinal stamps-per-customer buckets. */
export const ramp = ['#CDE9F7', '#8FD2F0', '#45AFDF', '#1778A8', '#0E4F70'];

export const positive = { fg: '#1F6B3A', bg: '#E3F1E6' };
export const negative = { fg: '#9A3412', bg: '#FBEAE3' };
export const neutral = { fg: '#5F6B76', bg: '#EEF2F5' };
export const caution = { fg: '#8A5A00', bg: '#FBEFD9' };

/** Shared chart geometry, so panels line up down the page. */
export const chart = {
  tick: { fill: ink.faint, fontSize: 12, fontFamily: bodyFont },
  gridStroke: surface.grid,
  height: 300,
  tallHeight: 340,
  margin: { top: 8, right: 8, left: -12, bottom: 0 }
};


/** Thousands separators everywhere a figure is shown. */
export const formatCount = (value: number) => Math.round(value).toLocaleString();

export const formatDateTime = (value: string | Date) =>
  new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

export const customerName = (firstName?: string | null, lastName?: string | null) => {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return name || 'Anonymous';
};
